/**
 * App Registry Store
 *
 * Manages the catalog of all apps (core + dynamic) and tracks
 * which dynamic apps the user has installed. Generates home screen
 * tiles from app manifests.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { registerHomeTile, registerHomeTiles, useHomeScreenStore, type HomeTile } from './homescreen';
import { useSessionStore } from './session';
import { isAdminRole } from '../utils/roles';
import { isDevConsoleAllowed } from './devConsoleAccess';
import type { AppManifest, AppCatalogEntry } from '../types/appManifest';
import { CORE_APP_IDS, isCoreApp } from '../types/appManifest';
import { createLogger } from '../utils/logger';

const logger = createLogger('AppRegistry');

interface AppRegistryState {
  /** All known app manifests (core + dynamic) */
  manifests: Record<string, AppManifest>;
  /** Set of installed dynamic app IDs (persisted per-user) */
  installedApps: string[];
  /** Whether the catalog has been fetched from server */
  catalogLoaded: boolean;
  /** Register manifests (core apps are always available) */
  registerManifests: (manifests: AppManifest[]) => void;
  /** Install a dynamic app */
  installApp: (appId: string) => void;
  /** Uninstall a dynamic app */
  uninstallApp: (appId: string) => void;
  /** Check if an app is available (core = always, dynamic = if installed) */
  isAppAvailable: (appId: string) => boolean;
  /** Get all available apps (core + installed dynamic) */
  getAvailableApps: () => AppManifest[];
  /** Get dynamic apps catalog (for store display) */
  getDynamicApps: () => AppCatalogEntry[];
  /** Set installed apps from server sync */
  setInstalledApps: (appIds: string[]) => void;
  /** Mark catalog as loaded */
  setCatalogLoaded: (loaded: boolean) => void;
}

export const useAppRegistryStore = create<AppRegistryState>()(
  devtools(
    persist(
      (set, get) => ({
        manifests: {},
        installedApps: [],
        catalogLoaded: false,

        registerManifests: (manifests: AppManifest[]) => {
          set((state) => {
            const newManifests = { ...state.manifests };
            manifests.forEach((m) => {
              newManifests[m.id] = m;
            });
            return { manifests: newManifests };
          });
          logger.debug(`Registered ${manifests.length} app manifests`);
        },

        installApp: (appId: string) => {
          const state = get();
          if (isCoreApp(appId)) return; // Core apps are always installed
          if (state.installedApps.includes(appId)) return;

          set({ installedApps: [...state.installedApps, appId] });
          logger.debug(`Installed app: ${appId}`);

          // Register tile immediately
          const manifest = state.manifests[appId];
          if (manifest?.tile) {
            registerTileFromManifest(manifest);
          }
        },

        uninstallApp: (appId: string) => {
          if (isCoreApp(appId)) return; // Cannot uninstall core apps

          set((state) => ({
            installedApps: state.installedApps.filter((id) => id !== appId),
          }));

          // Remove tile from home screen immediately
          useHomeScreenStore.getState().unregisterTile(appId);
          logger.debug(`Uninstalled app: ${appId}`);
        },

        isAppAvailable: (appId: string) => {
          if (isCoreApp(appId)) return true;
          return get().installedApps.includes(appId);
        },

        getAvailableApps: () => {
          const state = get();
          return Object.values(state.manifests).filter(
            (m) => isCoreApp(m.id) || state.installedApps.includes(m.id)
          );
        },

        getDynamicApps: () => {
          const state = get();
          return Object.values(state.manifests)
            .filter((m) => m.category === 'dynamic')
            .map((m) => ({
              ...m,
              installed: state.installedApps.includes(m.id),
            }));
        },

        setInstalledApps: (appIds: string[]) => {
          set({ installedApps: appIds });
        },

        setCatalogLoaded: (loaded: boolean) => {
          set({ catalogLoaded: loaded });
        },
      }),
      {
        name: 'app-registry-storage',
        partialize: (state) => ({
          installedApps: state.installedApps,
        }),
      }
    ),
    { name: 'AppRegistry' }
  )
);

/**
 * App IDs that should never appear as home screen tiles.
 * These are the launcher/auth screens themselves.
 */
const TILE_BLOCKLIST = new Set(['home', 'login']);

/**
 * Convert an AppManifest into a HomeTile and register it.
 */
function registerTileFromManifest(manifest: AppManifest): void {
  if (!manifest.tile) return;
  if (TILE_BLOCKLIST.has(manifest.id)) return;

  const tile: HomeTile = {
    id: manifest.id,
    label: manifest.tile.label,
    icon: manifest.icon,
    route: manifest.tile.route,
    section: manifest.tile.section,
    order: manifest.tile.order,
  };

  // Add visibility check for admin-only tiles
  if (manifest.tile.requiredRole === 'admin') {
    tile.visible = () => isAdminRole(useSessionStore.getState().user?.role);
  }

  // Developer tile visibility is controlled by dev console access settings
  if (manifest.id === 'developer') {
    tile.visible = () => isDevConsoleAllowed();
  }

  registerHomeTile(tile);
}

/**
 * Register tiles for all available apps (core + installed dynamic).
 * Called during app initialization.
 *
 * IMPORTANT: Skips apps that already have a registered tile to prevent
 * manifest data from overwriting authoritative tile definitions
 * (e.g. CORE_TILES dock placement). This avoids the "dock flash" where
 * catalog sync would re-register dock tiles with wrong section/order.
 */
export function registerAvailableAppTiles(): void {
  const state = useAppRegistryStore.getState();
  const homeState = useHomeScreenStore.getState();
  const availableApps = state.getAvailableApps();

  // Collect tiles to register in a single batch (avoids N individual re-renders)
  const tilesToRegister: HomeTile[] = [];
  availableApps.forEach((manifest) => {
    // Don't overwrite tiles already registered by registerDefaultTiles()
    if (homeState.items.has(manifest.id)) return;
    if (!manifest.tile) return;
    if (TILE_BLOCKLIST.has(manifest.id)) return;

    const tile: HomeTile = {
      id: manifest.id,
      label: manifest.tile.label,
      icon: manifest.icon,
      route: manifest.tile.route,
      section: manifest.tile.section,
      order: manifest.tile.order,
    };

    if (manifest.tile.requiredRole === 'admin') {
      tile.visible = () => isAdminRole(useSessionStore.getState().user?.role);
    }

    if (manifest.id === 'developer') {
      tile.visible = () => isDevConsoleAllowed();
    }

    tilesToRegister.push(tile);
  });

  if (tilesToRegister.length > 0) {
    registerHomeTiles(tilesToRegister);
  }

  logger.debug(`Registered ${tilesToRegister.length} new tiles (${availableApps.length} available, ${availableApps.length - tilesToRegister.length} already registered)`);
}

/**
 * Fetch the app catalog from server and register manifests.
 * Also syncs installed apps state when authenticated.
 *
 * IMPORTANT: The catalog endpoint returns user-specific `installedApps`
 * only when an auth token is provided. Without auth, the server returns
 * an empty list. We must NOT overwrite the locally-cached installed apps
 * with an empty server response, as that would wipe the user's home
 * screen tiles until the next authenticated sync.
 */
export async function loadAppCatalog(): Promise<void> {
  const store = useAppRegistryStore.getState();

  try {
    // Include session token so the server can return user-specific installed apps
    const token =
      localStorage.getItem('session_token') ??
      localStorage.getItem('pi_session_token');

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch('/api/apps/catalog', { headers });
    if (!response.ok) {
      logger.error('Failed to fetch app catalog:', response.status);
      return;
    }

    const data = await response.json();
    const manifests: AppManifest[] = data.apps || [];
    const installedIds: string[] = data.installedApps || [];

    store.registerManifests(manifests);

    // Only sync installed apps from server when the request was authenticated.
    // Without a token the server always returns installedApps: [], which would
    // incorrectly wipe the locally-cached installation data.
    if (token) {
      // Compute which apps were uninstalled on server so we can remove their tiles
      const previousInstalled = new Set(store.installedApps);
      store.setInstalledApps(installedIds);
      const newInstalled = new Set(installedIds);

      // Unregister tiles for dynamic apps removed from server's installed list
      previousInstalled.forEach((appId) => {
        if (!newInstalled.has(appId) && !isCoreApp(appId)) {
          useHomeScreenStore.getState().unregisterTile(appId);
        }
      });
    }

    store.setCatalogLoaded(true);

    // Register tiles for newly available apps (skips already-registered ones)
    registerAvailableAppTiles();

    logger.debug(`Catalog loaded: ${manifests.length} apps, ${installedIds.length} installed (authenticated: ${!!token})`);
  } catch (error) {
    logger.error('Error loading app catalog:', error);

    // Fallback: register tiles from locally cached manifests
    registerAvailableAppTiles();
  }
}

// React hooks
export function useAppRegistry() {
  return useAppRegistryStore();
}

export function useInstalledApps() {
  return useAppRegistryStore((s) => s.installedApps);
}

export function useDynamicApps() {
  return useAppRegistryStore((s) => s.getDynamicApps());
}

export function useIsAppInstalled(appId: string) {
  return useAppRegistryStore((s) => s.isAppAvailable(appId));
}
